'use client'

import { useEffect } from 'react'

// Map of font families to their Google Fonts URL
const fontUrls: Record<string, string> = {
  'Roboto': 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700&display=swap',
  'Lato': 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap',
  'Poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
  'Montserrat': 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap',
  'Nunito': 'https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700&display=swap',
  'Work Sans': 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap',
  'Plus Jakarta Sans': 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap',
  'DM Sans': 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;700&display=swap',
  'Merriweather': 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap',
  'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;900&display=swap',
  'Lora': 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap',
  'Source Serif Pro': 'https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@300;400;600;700&display=swap',
  'Crimson Text': 'https://fonts.googleapis.com/css2?family=Crimson+Text:wght@400;600;700&display=swap',
  'PT Serif': 'https://fonts.googleapis.com/css2?family=PT+Serif:wght@400;700&display=swap',
  'Bebas Neue': 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap',
  'Righteous': 'https://fonts.googleapis.com/css2?family=Righteous&display=swap',
  'Oswald': 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;700&display=swap',
  'Anton': 'https://fonts.googleapis.com/css2?family=Anton&display=swap',
  'Archivo Black': 'https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap',
  'JetBrains Mono': 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;700&display=swap',
  'Fira Code': 'https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;700&display=swap',
  'Source Code Pro': 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@300;400;500;700&display=swap',
  'IBM Plex Mono': 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;700&display=swap',
  'Space Mono': 'https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap',
  'Pacifico': 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap',
  'Caveat': 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap',
  'Dancing Script': 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap',
  'Satisfy': 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap',
  'Inter Display': 'https://fonts.googleapis.com/css2?family=Inter+Display:wght@300;400;500;600;700&display=swap',
  // Arabic Fonts
  'Cairo': 'https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&display=swap',
  'Tajawal': 'https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&display=swap',
  'Noto Naskh Arabic': 'https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&display=swap',
  'Amiri': 'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap',
  'Almarai': 'https://fonts.googleapis.com/css2?family=Almarai:wght@300;400;700;800&display=swap',
  'IBM Plex Sans Arabic': 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&display=swap',
  'Lateef': 'https://fonts.googleapis.com/css2?family=Lateef:wght@200;300;400;500;600;700;800&display=swap',
  'Rakkas': 'https://fonts.googleapis.com/css2?family=Rakkas&display=swap',
}

const loadedFonts = new Set<string>(['Inter']) // Inter is already loaded in layout

/**
 * Dynamically loads a Google Font on demand
 * Prevents loading all fonts upfront which slows down initial page load
 */
export function useDynamicFontLoader(fontFamily: string | undefined) {
  useEffect(() => {
    if (!fontFamily || fontFamily === 'Inter' || loadedFonts.has(fontFamily)) {
      return
    }

    const fontUrl = fontUrls[fontFamily]
    if (!fontUrl) {
      return
    }

    // Check if link already exists
    const existingLink = document.querySelector(`link[href="${fontUrl}"]`)
    if (existingLink) {
      loadedFonts.add(fontFamily)
      return
    }

    // Create and append link element
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontUrl
    document.head.appendChild(link)
    loadedFonts.add(fontFamily)
  }, [fontFamily])
}

export function DynamicFontLoader({ fontFamily }: { fontFamily?: string }) {
  useDynamicFontLoader(fontFamily)
  return null
}
